import { Injectable, ViewContainerRef } from '@angular/core';
import { MatMenuTrigger } from '@angular/material/menu';
import { SmallIconMenuAction } from '../shared/components/small-icon-menu/small-icon-menu.component';
import { ContactManagementService, ContactContext, ValidationResult } from './contact-management.service';
import { CustomerDetailsManagementService } from './customer-details-management.service';
import { InsuranceCompany, RelatedCustomer } from '../interfaces/insurance-company.interface';

export interface ChipMenuState {
  selectedChipText: string;
  selectedChipType: 'email' | 'phone';
  selectedChipContext: 'company' | 'customer';
  selectedCustomerId: number | null;
  currentMenuTrigger: MatMenuTrigger | null;
  editingContactValue: string;
  editTriggerX: number;
  editTriggerY: number;
  isUpdatingContact: boolean;
}

export interface EditMenuPosition {
  x: number;
  y: number;
}

/**
 * Service for managing chip menu actions and state in customer details
 */
@Injectable({
  providedIn: 'root'
})
export class ChipMenuActionsService {
  
  // Custom actions for chip menus
  readonly customMenuActions: SmallIconMenuAction[] = [
    { icon: 'content_copy', action: 'copy', label: 'Másolás' },
    { icon: 'edit', action: 'edit', label: 'Szerkesztés' },
    { icon: 'check', action: 'complete', label: 'Befejezés' },
    { icon: 'close', action: 'reject', label: 'Elutasítás' }
  ];

  constructor(
    private contactService: ContactManagementService,
    private managementService: CustomerDetailsManagementService
  ) {}

  /**
   * Handle chip click to open menu
   */
  handleChipClick(
    chipText: string,
    trigger: MatMenuTrigger,
    chipType: 'email' | 'phone' = 'email',
    context: 'company' | 'customer' = 'company',
    customerId?: number
  ): ChipMenuState {
    const state: ChipMenuState = {
      selectedChipText: chipText,
      selectedChipType: chipType,
      selectedChipContext: context,
      selectedCustomerId: customerId || null,
      currentMenuTrigger: trigger,
      editingContactValue: '',
      editTriggerX: -9999,
      editTriggerY: -9999,
      isUpdatingContact: false
    };

    trigger.openMenu();
    return state;
  }

  /**
   * Handle icon menu action
   */
  async handleIconMenuAction(
    action: SmallIconMenuAction,
    state: ChipMenuState,
    company: InsuranceCompany | null,
    individualCustomer: RelatedCustomer | null,
    onStateUpdate: (newState: Partial<ChipMenuState>) => void,
    onDataUpdate: () => void
  ): Promise<void> {
    switch (action.action) {
      case 'copy':
        await this.handleCopyAction(state);
        break;
      case 'edit':
        const editPosition = this.handleEditAction(state);
        onStateUpdate({ 
          editingContactValue: state.selectedChipText,
          editTriggerX: editPosition.x,
          editTriggerY: editPosition.y
        });
        break;
      case 'complete':
        await this.handleValidationAction(state, true, company, individualCustomer, onDataUpdate);
        break;
      case 'reject':
        await this.handleValidationAction(state, false, company, individualCustomer, onDataUpdate);
        break;
    }
  }

  /**
   * Handle copy action
   */
  private async handleCopyAction(state: ChipMenuState): Promise<void> {
    try {
      const result = await this.contactService.copyToClipboard(state.selectedChipText).toPromise();
      if (result?.success) {
        this.managementService.showSuccess(result.message);
      }
    } catch (error: any) {
      this.managementService.showError(error.message || 'Másolás sikertelen');
    } finally {
      this.closeCurrentMenu(state);
    }
  }

  /**
   * Handle edit action - calculate position and prepare for edit
   */
  private handleEditAction(state: ChipMenuState): EditMenuPosition {
    // Close current menu
    this.closeCurrentMenu(state);

    // Calculate position for edit menu
    let x = -9999;
    let y = -9999;

    // Note: Position calculation will be handled in component where we have access to the element
    // This service just provides the coordinates placeholder

    return { x, y };
  }

  /**
   * Handle validation action (complete/reject)
   */
  private async handleValidationAction(
    state: ChipMenuState,
    validStatus: boolean,
    company: InsuranceCompany | null,
    individualCustomer: RelatedCustomer | null,
    onDataUpdate: () => void
  ): Promise<void> {
    try {
      const context = this.buildContactContext(state, company, individualCustomer);
      const result = await this.contactService.validateContact(context, validStatus).toPromise();
      
      if (result?.success) {
        this.managementService.showSuccess(result.message);
        
        // Update local data
        if (company) {
          this.contactService.updateLocalContactData(
            company,
            state.selectedChipText,
            state.selectedChipType,
            'validation',
            validStatus
          );
        } else if (individualCustomer) {
          this.contactService.updateIndividualCustomerContactData(
            individualCustomer,
            state.selectedChipText,
            state.selectedChipType,
            'validation',
            validStatus
          );
        }

        // Always trigger data update for change detection
        onDataUpdate();

        // Create interaction log
        const ownership = this.contactService.findContactOwnership(
          company,
          state.selectedChipText,
          state.selectedChipType
        );
        
        const targetCompanyId = ownership.companyId || (individualCustomer ? undefined : company?.id);
        const targetCustomerId = ownership.customerId || (individualCustomer ? individualCustomer.id : undefined);

        if (targetCompanyId || targetCustomerId) {
          this.contactService.createValidationInteraction(
            state.selectedChipText,
            state.selectedChipType,
            validStatus,
            targetCompanyId,
            targetCustomerId
          ).subscribe({
            next: () => {}, // Interaction already handled by onDataUpdate() above
            error: (error) => console.error('Error creating interaction:', error)
          });
        }
      }
    } catch (error: any) {
      this.managementService.showError(error.message || 'Hiba történt a státusz frissítésekor');
    } finally {
      this.closeCurrentMenu(state);
    }
  }

  /**
   * Handle contact edit save
   */
  async handleContactEditSave(
    newValue: string,
    state: ChipMenuState,
    company: InsuranceCompany | null,
    individualCustomer: RelatedCustomer | null,
    onStateUpdate: (newState: Partial<ChipMenuState>) => void
  ): Promise<boolean> {
    // Prevent duplicate calls
    if (state.isUpdatingContact) {
      return false;
    }

    onStateUpdate({ isUpdatingContact: true });

    try {
      const context = this.buildContactContext(state, company, individualCustomer);
      const result = await this.contactService.editContact(context, newValue).toPromise();
      
      if (result?.success) {
        this.managementService.showSuccess(result.message);
        
        // Update local data
        if (company) {
          this.contactService.updateLocalContactData(
            company,
            state.selectedChipText,
            state.selectedChipType,
            'edit',
            newValue
          );
        } else if (individualCustomer) {
          this.contactService.updateIndividualCustomerContactData(
            individualCustomer,
            state.selectedChipText,
            state.selectedChipType,
            'edit',
            newValue
          );
        }

        // Update state
        onStateUpdate({ 
          selectedChipText: newValue,
          editingContactValue: '',
          isUpdatingContact: false
        });

        return true;
      }
    } catch (error: any) {
      this.managementService.showError(error.message || 'Hiba történt a kontakt frissítése során');
    }

    onStateUpdate({ isUpdatingContact: false });
    return false;
  }

  /**
   * Handle contact edit cancel
   */
  handleContactEditCancel(onStateUpdate: (newState: Partial<ChipMenuState>) => void): void {
    onStateUpdate({ 
      editingContactValue: '',
      isUpdatingContact: false
    });
  }

  /**
   * Build contact context from current state
   */
  private buildContactContext(
    state: ChipMenuState,
    company: InsuranceCompany | null,
    individualCustomer: RelatedCustomer | null
  ): ContactContext {
    const context: ContactContext = {
      contactValue: state.selectedChipText,
      contactType: state.selectedChipType,
      context: state.selectedChipContext
    };

    if (state.selectedChipContext === 'customer' && state.selectedCustomerId) {
      context.customerId = state.selectedCustomerId;
    } else if (company?.id && !individualCustomer) {
      context.companyId = company.id;
    } else if (individualCustomer?.id) {
      context.customerId = individualCustomer.id;
    }

    return context;
  }

  /**
   * Close current menu
   */
  private closeCurrentMenu(state: ChipMenuState): void {
    if (state.currentMenuTrigger) {
      state.currentMenuTrigger.closeMenu();
    }
  }

  /**
   * Create initial chip menu state
   */
  createInitialState(): ChipMenuState {
    return {
      selectedChipText: '',
      selectedChipType: 'email',
      selectedChipContext: 'company',
      selectedCustomerId: null,
      currentMenuTrigger: null,
      editingContactValue: '',
      editTriggerX: -9999,
      editTriggerY: -9999,
      isUpdatingContact: false
    };
  }
}